/**
 * Quand une notification est remise (docs/02-fonctionnel.md §10).
 *
 * Logique volontairement pure et isolée : c'est elle qui décide qu'un parent ne sera pas
 * réveillé à 2h du matin, et cette décision doit être vérifiable sans base ni horloge
 * réelle. Elle traite trois réglages qui se composent, dans cet ordre :
 *
 *   1. **alerte urgente** — court-circuite tout le reste. Seule exception du produit ;
 *   2. **digest** — regroupe les notifications à une heure locale choisie ;
 *   3. **heures silencieuses** — décale à la fin de la plage ce qui y tomberait.
 *
 * L'ordre compte : un digest réglé en pleine nuit doit être rattrapé par les heures
 * silencieuses, pas l'inverse.
 *
 * **Deux instants, pas un.** L'appareil et l'e-mail ne sont pas le même canal et n'ont pas
 * la même tolérance : une notification sur l'écran s'écarte d'un geste, un e-mail reste
 * dans la boîte jusqu'à ce qu'on le traite. Un parent peut donc vouloir être prévenu au fil
 * de l'eau sur son téléphone **et** ne recevoir qu'un seul e-mail par jour. `deliveryInstant`
 * décide du premier, `mailInstant` du second ; ils ne diffèrent que si le parent l'a demandé.
 */
import { DEFAULT_TIME_ZONE, addDays, instantToWallTime, wallTimeToInstant, } from '../custody/time-zone.js';
export const DIGEST_FREQUENCIES = ['immediate', 'daily'];
/** Valeurs du cadrage : silence 21h–8h, remise au fil de l'eau (docs/02 §10). */
export const DEFAULT_NOTIFICATION_PREFERENCES = {
    quietHours: { start: 21, end: 8 },
    digest: 'immediate',
    // Au fil de l'eau, comme avant l'existence du réglage : le parent qui ne veut qu'un
    // résumé quotidien le demande, on ne le lui impose pas. Un défaut plus silencieux
    // aurait fait disparaître, sans le dire, des e-mails que des comptes existants
    // recevaient la veille.
    mailDigest: 'immediate',
    digestHour: 8,
    timeZone: DEFAULT_TIME_ZONE,
    // Actives par défaut : l'urgence sert à joindre quelqu'un qui ne regarde pas son
    // téléphone, et un dispositif qu'il faut avoir pensé à activer avant d'en avoir besoin
    // ne sert à rien. On les éteint après un abus, on ne les allume pas avant.
    urgentSound: true,
    urgentTakeover: true,
};
export class NotificationPreferencesError extends TypeError {
}
/**
 * Lit un réglage stocké en JSON, en le complétant par les défauts.
 *
 * Un réglage invalide lève, plutôt que d'être silencieusement ramené au défaut : un parent
 * qui croit avoir désactivé ses notifications de nuit et les reçoit quand même n'a aucun
 * moyen de s'en apercevoir avant d'être réveillé.
 */
export function parseNotificationPreferences(raw) {
    if (raw === undefined || raw === null)
        return { ...DEFAULT_NOTIFICATION_PREFERENCES };
    if (typeof raw !== 'object' || Array.isArray(raw)) {
        throw new NotificationPreferencesError('Réglage de notification illisible.');
    }
    const source = raw;
    const preferences = { ...DEFAULT_NOTIFICATION_PREFERENCES };
    for (const champ of ['digest', 'mailDigest']) {
        if (!(champ in source))
            continue;
        const frequence = source[champ];
        if (!DIGEST_FREQUENCIES.includes(frequence)) {
            throw new NotificationPreferencesError(`Fréquence de digest inconnue : ${String(frequence)}`);
        }
        preferences[champ] = frequence;
    }
    if ('digestHour' in source) {
        preferences.digestHour = assertHour(source.digestHour, 'digestHour');
    }
    if ('timeZone' in source) {
        if (typeof source.timeZone !== 'string' || source.timeZone.length === 0) {
            throw new NotificationPreferencesError('Fuseau horaire invalide.');
        }
        preferences.timeZone = source.timeZone;
    }
    for (const champ of ['urgentSound', 'urgentTakeover']) {
        if (!(champ in source))
            continue;
        if (typeof source[champ] !== 'boolean') {
            throw new NotificationPreferencesError(`${champ} doit être un booléen.`);
        }
        preferences[champ] = source[champ];
    }
    if ('quietHours' in source) {
        const quiet = source.quietHours;
        if (quiet === null) {
            preferences.quietHours = null;
        }
        else if (typeof quiet === 'object' && quiet !== null) {
            const { start, end } = quiet;
            const debut = assertHour(start, 'quietHours.start');
            const fin = assertHour(end, 'quietHours.end');
            if (debut === fin) {
                throw new NotificationPreferencesError('Une plage silencieuse dont le début et la fin coïncident est ambiguë : ' +
                    'aucun silence, ou silence permanent. Choisissez deux heures distinctes.');
            }
            preferences.quietHours = { start: debut, end: fin };
        }
        else {
            throw new NotificationPreferencesError('Plage silencieuse illisible.');
        }
    }
    return preferences;
}
function assertHour(value, champ) {
    if (typeof value !== 'number' || !Number.isInteger(value) || value < 0 || value > 23) {
        throw new NotificationPreferencesError(`${champ} doit être une heure entière entre 0 et 23 (reçu : ${String(value)}).`);
    }
    return value;
}
/**
 * Instant auquel la notification doit partir.
 *
 * `bypassQuietHours` est réservé à l'alerte urgente : rien d'autre ne franchit le silence,
 * et surtout pas une relance. Le paramètre est nommé pour que son unique appelant légitime
 * saute aux yeux en revue.
 */
export function deliveryInstant(createdAt, preferences, options = {}) {
    if (options.bypassQuietHours)
        return createdAt;
    const base = preferences.digest === 'daily'
        ? nextLocalHour(createdAt, preferences.digestHour, preferences.timeZone)
        : createdAt;
    return outOfQuietHours(base, preferences);
}
/**
 * Instant auquel l'**e-mail** doit partir. Le même calcul, une cadence de plus.
 *
 * Composée par le maximum et non par le dernier réglage lu : `mailDigest` ne peut que
 * retenir l'e-mail plus longtemps que `digest`, jamais le libérer plus tôt. Un parent qui
 * a réglé le digest global sur « une fois par jour » ne se verra donc pas rendre les
 * e-mails au fil de l'eau parce que la cadence de l'e-mail, elle, est restée au défaut.
 *
 * `bypassQuietHours` reste réservé à l'alerte urgente, et il emporte tout : celle-ci part
 * par les deux canaux à l'instant où elle est déposée. Retenir jusqu'au lendemain matin
 * l'e-mail d'une alerte que le téléphone vient de faire sonner n'aurait aucun sens — et
 * l'e-mail est le seul canal qui reste quand le téléphone est resté sur la table.
 */
export function mailInstant(createdAt, preferences, options = {}) {
    const quotidien = preferences.digest === 'daily' || preferences.mailDigest === 'daily';
    return deliveryInstant(createdAt, { ...preferences, digest: quotidien ? 'daily' : 'immediate' }, options);
}
/** Prochaine occurrence — au sens large — d'une heure locale pleine. */
function nextLocalHour(from, hour, timeZone) {
    const { date } = instantToWallTime(from, timeZone);
    const aujourdhui = wallTimeToInstant(date, { hour, minute: 0 }, timeZone);
    if (aujourdhui.getTime() >= from.getTime())
        return aujourdhui;
    return wallTimeToInstant(addDays(date, 1), { hour, minute: 0 }, timeZone);
}
/** Repousse à la fin du silence un instant qui y tomberait. */
function outOfQuietHours(instant, preferences) {
    const quiet = preferences.quietHours;
    if (!quiet)
        return instant;
    const { date, time } = instantToWallTime(instant, preferences.timeZone);
    if (!isQuiet(time.hour, quiet))
        return instant;
    // La plage franchit minuit lorsque `start > end` : la fin tombe alors le lendemain,
    // sauf si l'instant est déjà passé dans la partie « petit matin » de la plage.
    const finLeLendemain = quiet.start > quiet.end && time.hour >= quiet.start;
    const jourDeFin = finLeLendemain ? addDays(date, 1) : date;
    return wallTimeToInstant(jourDeFin, { hour: quiet.end, minute: 0 }, preferences.timeZone);
}
function isQuiet(hour, quiet) {
    return quiet.start > quiet.end
        ? hour >= quiet.start || hour < quiet.end // franchit minuit
        : hour >= quiet.start && hour < quiet.end;
}
//# sourceMappingURL=delivery.js.map